import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AddImeiUploadIcon, CloseIcon, DeleteBin6LineIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import deviceApi from '~/apis/device';
import locationApi from '~/apis/location';
import { RoleIdUserEnum } from '~/utils/enum';
import { handleApiError, notify, readExcel } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';

type TransferDeviceDialogProps = {
  open: boolean;
  currentUser: any;
  onClose: () => void;
  onTransferred: () => void;
};

const TransferDeviceDialog = ({ open, currentUser, onClose, onTransferred }: TransferDeviceDialogProps) => {
  const { t } = useTranslation('admin/device');
  const [action, setAction] = useState<'transfer' | 'reclaim'>('transfer');
  const [fileName, setFileName] = useState('');
  const [imeiInput, setImeiInput] = useState('');
  const [imeis, setImeis] = useState<string[]>([]);
  const [provinceId, setProvinceId] = useState('');
  const [wardId, setWardId] = useState('');
  const [note, setNote] = useState('');
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = Number(currentUser?.roleid) === RoleIdUserEnum.QUAN_TRI;
  const isProvinceRole = Number(currentUser?.roleid) === RoleIdUserEnum.TINH_THANH;
  const isProvinceGroup = Number(currentUser?.groupid) === 3;

  useEffect(() => {
    if (!open) return;

    setAction('transfer');
    setFileName('');
    setImeiInput('');
    setImeis([]);
    setProvinceId(String(currentUser?.province_id || ''));
    setWardId('');
    setNote('');

    const loadProvinces = async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
          }))
        );
      } catch (error) {
        handleApiError(error);
      }
    };

    loadProvinces();
  }, [open, currentUser?.province_id]);

  useEffect(() => {
    if (!open || action !== 'transfer' || !provinceId) {
      setWardOptions([]);
      return;
    }

    const loadWards = async () => {
      try {
        setIsLoading(true);
        const response = await locationApi.getListWards({ province: provinceId });
        setWardOptions(
          response.data.map((item) => ({
            id: item.code,
            name: item.name,
            data0: item.name,
            data1: item.name_with_type,
            data2: item.type,
            data3: item.parent_code,
          }))
        );
      } catch (error) {
        setWardOptions([]);
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWards();
  }, [open, action, provinceId]);

  const addImeis = (value: string) => {
    const newImeis = value
      .split(/[,\n\r\t]+/)
      .map((imei) => imei.trim())
      .filter(Boolean);
    setImeis((current) => Array.from(new Set([...current, ...newImeis])));
    setImeiInput('');
    setFileName('');
  };

  const handleFile = async (file: File) => {
    try {
      const rows = await readExcel(file);
      const values = rows
        .map((row: any) => String(row.IMEI ?? row.imei ?? '').trim())
        .filter(Boolean)
        .join('\n');
      if (!values) {
        notify('error', t('transferDeviceDialog.fileNoImei'));
        return;
      }
      addImeis(values);
      setFileName(file.name);
    } catch (error) {
      handleApiError(error);
    }
  };

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (files) => files[0] && handleFile(files[0]),
  });

  const handleSubmit = async () => {
    if (!imeis.length) {
      notify('error', t('transferDeviceDialog.imeiRequired'));
      return;
    }
    if (action === 'transfer' && (!provinceId || !wardId)) {
      notify('error', t('transferDeviceDialog.locationRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      await deviceApi.transferDevices({
        action,
        imeis,
        ...(action === 'transfer' ? { provinceId, wardId } : {}),
        note,
      });
      notify('success', t('transferDeviceDialog.success'));
      onClose();
      onTransferred();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
      <DialogTitle className='flex items-center justify-between bg-[#123B6D] text-white'>
        <Typography className='text-xl font-semibold text-white'>{t('transferDeviceDialog.title')}</Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting} className='text-white'>
          <CloseIcon className='h-5 w-5' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='flex flex-col gap-4 pt-6 mt-3'>
        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('transferDeviceDialog.action')}</Typography>
          <BaseSelect
            options={[
              { id: 'transfer', name: t('transferDeviceDialog.transfer') },
              { id: 'reclaim', name: t('transferDeviceDialog.reclaim') },
            ]}
            value={action}
            onChange={(event) => {
              setAction(event.target.value);
              setWardId('');
            }}
            isSortEnable={false}
          />
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('transferDeviceDialog.upload')}</Typography>
          <Box
            {...getRootProps()}
            className={`flex min-h-24 cursor-pointer items-center justify-center rounded-xl border border-dashed p-4 ${isDragAccept ? 'border-[#123B6D] bg-[#EAF1F9]' : 'border-[#D7DEE7]'}`}
          >
            <Box component='input' {...getInputProps()} />
            <Box className='flex items-center gap-3 text-[#7C8796]'>
              <AddImeiUploadIcon className='h-7 w-7' />
              <Typography>{fileName || t('transferDeviceDialog.uploadHint')}</Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('transferDeviceDialog.imeiInput')}</Typography>
          <Box className='flex gap-3'>
            <TextField
              value={imeiInput}
              onChange={(event) => setImeiInput(event.target.value)}
              fullWidth
              multiline
              minRows={1}
            />
            <Button variant='outlined' onClick={() => addImeis(imeiInput)} className='min-w-24 self-end'>
              {t('transferDeviceDialog.add')}
            </Button>
          </Box>
        </Box>

        <Box className='rounded-xl border border-[#EBF1F8]'>
          <Box className='flex items-center justify-between border-b border-[#EBF1F8] px-4 py-2'>
            <Typography className='font-medium'>{t('transferDeviceDialog.selectedImeis')}</Typography>
            <Typography className='text-[#7C8796]'>{imeis.length}</Typography>
          </Box>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>{t('transferDeviceDialog.number')}</TableCell>
                <TableCell>IMEI</TableCell>
                <TableCell align='center'>{t('transferDeviceDialog.delete')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {imeis.length ? imeis.map((imei, index) => (
                <TableRow key={imei}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{imei}</TableCell>
                  <TableCell align='center'>
                    <IconButton size='small' onClick={() => setImeis((current) => current.filter((item) => item !== imei))}>
                      <DeleteBin6LineIcon className='h-4 w-4' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={3} align='center'>{t('transferDeviceDialog.empty')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {action === 'transfer' && (
          <Box className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <Box>
              <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('province_id')}</Typography>
              <BaseSelect
                options={isAdmin ? provinceOptions : provinceOptions.filter((item) => String(item.id) === provinceId)}
                value={provinceId}
                placeholder={t('select_province_id')}
                onChange={(event) => {
                  setProvinceId(event.target.value);
                  setWardId('');
                }}
                disabled={!isAdmin || isProvinceRole || isProvinceGroup}
                isSearchEnable
              />
            </Box>
            <Box>
              <Typography className='mb-2 text-sm font-medium text-[#161A1F]'>{t('ward_id')}</Typography>
              <BaseSelect
                options={wardOptions}
                value={wardId}
                placeholder={t('select_ward_id')}
                onChange={(event) => setWardId(event.target.value)}
                disabled={!isAdmin && !isProvinceRole}
                isSearchEnable
              />
            </Box>
          </Box>
        )}

        <TextField
          label={t('transferDeviceDialog.note')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          multiline
          minRows={2}
          fullWidth
        />

        <Button variant='contained' onClick={handleSubmit} disabled={isLoading || isSubmitting} className='bg-[#123B6D] hover:bg-[#0E2F58]'>
          {isSubmitting ? <CircularProgress size={22} color='inherit' /> : t('transferDeviceDialog.submit')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default TransferDeviceDialog;